import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAYMENT_OPTIONS = [
  { value: "__none__", label: "—" },
  { value: "on_delivery", label: "Átvételkor" },
  { value: "transfer_8", label: "8 nap átutalás" },
  { value: "transfer_10", label: "10 nap átutalás" },
  { value: "transfer_15", label: "15 nap átutalás" },
];

const emptyForm = {
  name: "",
  short_name: "",
  partner_type: "",
  tax_id: "",
  hq_address: "",
  order_email: "",
  bank_account_number: "",
  bank_name: "",
  payment_terms: "",
  notes: "",
  contacts: [],
};

export function PartnerFormDialog({ open, onOpenChange, partner, onSaved }) {
  const isEdit = !!partner;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (partner) {
      setForm({
        name: partner.name || "",
        short_name: partner.short_name || "",
        partner_type: partner.partner_type || "",
        tax_id: partner.tax_id || "",
        hq_address: partner.hq_address || "",
        order_email: partner.order_email || "",
        bank_account_number: partner.bank_account_number || "",
        bank_name: partner.bank_name || "",
        payment_terms: partner.payment_terms || "",
        notes: partner.notes || "",
        contacts: (partner.contacts || []).map((c) => ({ ...c })),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, partner]);

  const addContact = () => {
    setForm((f) => ({
      ...f,
      contacts: [...f.contacts, { name: "", phone: "", email: "" }],
    }));
  };
  const removeContact = (idx) => {
    setForm((f) => ({
      ...f,
      contacts: f.contacts.filter((_, i) => i !== idx),
    }));
  };
  const updateContact = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Cégnév megadása kötelező.");
    if (!form.partner_type) return setErr("Típus kiválasztása kötelező.");
    const oe = (form.order_email || "").trim();
    if (oe && !oe.includes("@")) {
      return setErr("A rendelési e-mail cím nem érvényes.");
    }
    for (const c of form.contacts) {
      if (!c.name.trim()) {
        return setErr("Minden kapcsolattartónál add meg a nevet vagy töröld a sort.");
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        short_name: form.short_name.trim() || null,
        partner_type: form.partner_type,
        tax_id: form.tax_id.trim() || null,
        hq_address: form.hq_address.trim() || null,
        order_email: form.order_email.trim() || null,
        bank_account_number: form.bank_account_number.trim() || null,
        bank_name: form.bank_name.trim() || null,
        payment_terms: form.payment_terms || null,
        notes: form.notes.trim() || null,
        contacts: form.contacts
          .filter((c) => c.name.trim())
          .map((c) => ({
            name: c.name.trim(),
            phone: (c.phone || "").trim() || null,
            email: (c.email || "").trim() || null,
          })),
      };
      const { data } = isEdit
        ? await api.patch(`/partners/${partner._id}`, payload)
        : await api.post("/partners", payload);
      toast.success(isEdit ? "Partner frissítve." : "Partner létrehozva.");
      onSaved(data, isEdit ? "edit" : "create");
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="partner-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Partner szerkesztése" : "Új partner"}</DialogTitle>
          <DialogDescription>Cég-, banki- és kapcsolattartói adatok.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-5">
          <section className="space-y-3">
            <SectionTitle>Cég adatai</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Cégnév *">
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="partner-name" />
              </Field>
              <Field label="Rövid név">
                <Input value={form.short_name} onChange={(e) => setForm((f) => ({ ...f, short_name: e.target.value }))} />
              </Field>
              <Field label="Típus *">
                <Select value={form.partner_type} onValueChange={(v) => setForm((f) => ({ ...f, partner_type: v }))}>
                  <SelectTrigger data-testid="partner-type-trigger"><SelectValue placeholder="Válassz…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="supplier">Beszállító</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Adószám">
                <Input value={form.tax_id} onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))} placeholder="pl. 12345678-2-41" />
              </Field>
              <Field label="Cég székhelye" className="sm:col-span-2">
                <Input value={form.hq_address} onChange={(e) => setForm((f) => ({ ...f, hq_address: e.target.value }))} placeholder="1051 Budapest, Példa utca 1." />
              </Field>
              <Field label="Rendelési e-mail cím" className="sm:col-span-2">
                <Input
                  type="email"
                  value={form.order_email}
                  onChange={(e) => setForm((f) => ({ ...f, order_email: e.target.value }))}
                  placeholder="pl. rendeles@beszallito.hu"
                  data-testid="partner-order-email"
                />
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Beszállítónál kötelező rendelés indításához. Ha üres, a rendszer
                  csak a beszállító saját weboldalán keresztül engedi a rendelést.
                </span>
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>Banki adatok és fizetés</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Bank neve">
                <Input value={form.bank_name} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} placeholder="pl. OTP Bank" />
              </Field>
              <Field label="Számlaszám">
                <Input value={form.bank_account_number} onChange={(e) => setForm((f) => ({ ...f, bank_account_number: e.target.value }))} placeholder="12345678-12345678-12345678" />
              </Field>
              <Field label="Fizetési mód" className="sm:col-span-2">
                <Select
                  value={form.payment_terms || "__none__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, payment_terms: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Válassz…" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionTitle>Kapcsolattartók</SectionTitle>
              <Button type="button" size="sm" variant="outline" onClick={addContact} data-testid="add-contact-button">
                <UserPlus className="h-3.5 w-3.5" /> Új kapcsolattartó
              </Button>
            </div>
            {form.contacts.length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                Még nincs kapcsolattartó.
              </p>
            ) : (
              <div className="space-y-3">
                {form.contacts.map((c, idx) => (
                  <div key={idx} className="rounded-lg border border-border bg-muted/20 p-3" data-testid={`contact-row-${idx}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                        Kapcsolattartó #{idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeContact(idx)}
                        data-testid={`remove-contact-${idx}`}
                        className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Field label="Név *">
                        <Input value={c.name} onChange={(e) => updateContact(idx, "name", e.target.value)} data-testid={`contact-name-${idx}`} />
                      </Field>
                      <Field label="Telefon">
                        <Input value={c.phone || ""} onChange={(e) => updateContact(idx, "phone", e.target.value)} type="tel" />
                      </Field>
                      <Field label="E-mail">
                        <Input value={c.email || ""} onChange={(e) => updateContact(idx, "email", e.target.value)} type="email" />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {err && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="partner-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </h3>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={`block space-y-1.5 ${className || ""}`}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
